import { GOOGLE_TAG_MANAGER_CONTAINER_ID } from "@shared/env";
import {
  AnalyticEventTypes,
  BaseAnalyticsType,
  GTMDataLayerName,
} from "./types/analytics-types";
import {
  addToCartAnalyticsData,
  beginCheckoutAnalyticsData,
  GoogleEventNames,
  postPurchaseAnalyticsData,
  removeFromCartAnalyticsData,
  viewItemsAnalyticsData,
} from "./utils/google";

declare global {
  // eslint-disable-next-line vars-on-top, no-var
  var GTMDataLayers: Record<string, boolean>;
  // eslint-disable-next-line vars-on-top, no-var
  var currentGTMDataLayer: GTMDataLayerName;
}

globalThis.GTMDataLayers = {};

// manage current venue GTM, will be updated once init is called
globalThis.currentGTMDataLayer = `dataLayer_GTM_`;

// this will be automatically loaded based on _document.tsx with the following data layer name
const showpassDataLayerName: GTMDataLayerName = `dataLayer_GTM_${
  (GOOGLE_TAG_MANAGER_CONTAINER_ID || "").split("-")[1]
}` as GTMDataLayerName;

export interface GTMAnalyticsType extends BaseAnalyticsType {
  viewContent: AnalyticEventTypes["viewContent"];
  viewItems: AnalyticEventTypes["viewItems"];
  removeFromCart: AnalyticEventTypes["removeFromCart"];
  awConversion: AnalyticEventTypes["awConversion"];
  disable: (id: string) => void;
}

// Cleaned up default injection script to work with API and multiple dataLayers
const loadGTM = (gtmIdUniqueCode: string) => {
  // Run the GTM loading only after the DOM is fully ready
  const onDomContentLoaded = () => {
    const windowObject = window as Window;
    const documentObject = document as Document;
    const scriptTag = "script";
    const dataLayerName: GTMDataLayerName = `dataLayer_GTM_${gtmIdUniqueCode}`;
    windowObject[dataLayerName] = windowObject[dataLayerName] || [];
    windowObject[dataLayerName].push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    const firstScriptTag = documentObject.getElementsByTagName(scriptTag)[0];
    const newScriptElement = documentObject.createElement(
      scriptTag
    ) as HTMLScriptElement;
    const dl = gtmIdUniqueCode !== "dataLayer" ? `&l=${dataLayerName}` : "";
    newScriptElement.async = true;
    newScriptElement.src = `https://www.googletagmanager.com/gtm.js?id=GTM-${gtmIdUniqueCode}${dl}`;
    newScriptElement.id = "gtm-showpass-client";

    // Get nonce from the meta tag in the document
    const nonceMetaTag = documentObject.querySelector("meta[name='csp-nonce']");
    const nonce = nonceMetaTag?.getAttribute("content");

    if (nonce) {
      newScriptElement.setAttribute("nonce", nonce);
      newScriptElement.setAttribute("data-ngtm", nonce);
    }

    firstScriptTag.parentNode?.insertBefore(newScriptElement, firstScriptTag);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onDomContentLoaded);
  } else {
    onDomContentLoaded();
  }
};

function isGtmLoaded(layer: GTMDataLayerName) {
  window[layer] = window[layer] || [];
  const gtmStartedEvent = window[layer].find((element) => element["gtm.start"]); // gtm.start event fired on load
  if (!gtmStartedEvent) {
    return false; // Not even the GTM inline config has executed
  }
  return true; // GTM is fully loaded and working
}

type DataLayerEventWithCallback = Record<string, unknown> & {
  eventCallback?(): void;
};

function pushToDataLayers(data: DataLayerEventWithCallback) {
  // Venue layer
  if (isGtmLoaded(currentGTMDataLayer)) window[currentGTMDataLayer].push(data);
  // Showpass layer
  if (isGtmLoaded(showpassDataLayerName))
    window[showpassDataLayerName].push(data);
}

/**
 * Pushes ecommerce events with proper data clearing to prevent race conditions.
 * Uses a custom "ecommerce_clear" event with eventCallback to ensure GTM clears old ecommerce
 * data before processing new events, preventing merged item arrays from previous events.
 */
function pushEcommerceEvent(
  eventName: string,
  ecommerceData: Record<string, unknown>
) {
  let callbackFired = false;
  pushToDataLayers({
    event: "ecommerce_clear",
    ecommerce: null,
    eventCallback() {
      if (callbackFired) return;
      callbackFired = true;
      pushToDataLayers({
        event: eventName,
        ecommerce: ecommerceData,
      });
    },
  });
}

const gtm: GTMAnalyticsType = {
  init: (id: string) => {
    if (!id) {
      currentGTMDataLayer = "dataLayer_GTM_";
      return;
    }

    delete window[`ga-disable-${id}`]; // if tag was disabled, re-enable it

    // Just keep the GTM code for easy layer management
    const gtmIdUniqueCode = id.split("-")[1];
    // If already loaded, no need to init again but set the current data layer to this id
    if (GTMDataLayers[gtmIdUniqueCode]) {
      currentGTMDataLayer = `dataLayer_GTM_${gtmIdUniqueCode}`;
      return;
    }

    try {
      loadGTM(gtmIdUniqueCode);
      currentGTMDataLayer = `dataLayer_GTM_${gtmIdUniqueCode}`;
      GTMDataLayers[gtmIdUniqueCode] = true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("GTM Init: Venue ID error", e);
    }
  },

  pageView() {
    // Automatically done by Google via Enhanced Event Measurement, provided an organizer has enabled it
  },
  viewContent(item) {
    pushToDataLayers({
      event: GoogleEventNames.ViewContent,
      ecommerce: {
        content_ids: [item.id],
        content_type: item.type ?? "product_group",
        content_name: item.name,
      },
    });
  },

  viewItems(ttset, venue) {
    const viewItemsData = viewItemsAnalyticsData(
      ttset,
      venue?.name,
      venue?.currency
    );
    pushEcommerceEvent(GoogleEventNames.ViewItem, viewItemsData);
  },

  addToCart(group, count, venue) {
    const addToCartData = addToCartAnalyticsData(
      group,
      count,
      venue?.name,
      venue?.currency
    );
    pushEcommerceEvent(GoogleEventNames.AddToCart, addToCartData);
  },

  removeFromCart() {
    const removeFromCartData = removeFromCartAnalyticsData();
    pushEcommerceEvent(GoogleEventNames.RemoveFromCart, removeFromCartData);
  },

  beginCheckout(basket, venue) {
    const beginCheckoutData = beginCheckoutAnalyticsData(
      basket,
      venue?.name,
      venue?.currency
    );
    pushEcommerceEvent(GoogleEventNames.BeginCheckout, beginCheckoutData);
  },

  postPurchase(basket, venue) {
    const postPurchaseData = postPurchaseAnalyticsData(
      basket,
      venue?.name,
      venue?.currency
    );
    pushEcommerceEvent(GoogleEventNames.PostPurchase, postPurchaseData);
  },

  awConversion(conversionID, conversionLabel, item, ...args) {
    pushToDataLayers({
      event: GoogleEventNames.AWConversion,
      send_to: `${conversionID}/${conversionLabel}`,
      ...item,
      ...args,
    });
  },

  grantConsent() {
    pushToDataLayers({
      event: GoogleEventNames.ConsentGranted,
      ad_storage: "granted",
      analytics_storage: "granted",
    });
  },

  revokeConsent() {
    pushToDataLayers({
      event: GoogleEventNames.ConsentRevoked,
      ad_storage: "denied",
      analytics_storage: "denied",
    });
  },

  track(action, ...args) {
    pushToDataLayers({
      event: action,
      ...args,
    });
  },

  disable(id: string) {
    window[`ga-disable-${id}`] = true;
  },
};

export default gtm;
